import Util from '../../Common/Util';

export default class Counsel {
    constructor() {
        this.$table = null; // 클래스 내부에서 사용하도록 this.$table로 변경
        // ⚠ '경고'는 **여기 없다.** 경고는 제재관리에서만 만든다 (2026-08).
        //   상담↔제재 양방향 동기화를 걷어내면서 상담 유형에서 뺐고, 대신 서버가 제재의
        //   경고를 목록에 시간순으로 끼워 넣는다 (CounselController::mergeWarnings).
        //   끼워 넣은 줄은 `src='penalty'` 라 여기서 수정·삭제 대상이 아니다.
        this.types = {
            '상담': '#ffffff',
            '가입': '#fffce3',
            '추가가입': '#fcf6bd',
            '연장': '#f7ffde',
            '정지': '#e8eefa',
            '탈퇴': '#edf4fc',
            '중요': '#ffffff',
            '기타': '#ffffff',
        };
        // 목록에 끼워 넣은 제재 경고의 줄 색 (선택지에는 없지만 그려야 한다)
        // 아직 집행중(안 풀림)인 경고만 경고색 — 급한 것만 눈에 띄어야 한다. 풀린 경고는 부여·해제
        // 두 줄 다 회색으로 가라앉힌다(is_case_closed 로 가른다) — 지나간 일까지 핑크로 남으면
        // 지금 걸려있는 건지 이미 끝난 건지 내용을 읽기 전엔 구분이 안 됐다.
        this.WARN_BG = '#ffc1cc';
        this.WARN_CLOSED_BG = '#e9ecef';
        this.isEdit = false;

        // 다루는 회원. 기본은 편집중인 회원(window.MEMBER)이고, 다른 사람의 상담을 열 때만
        // `openCounselModal(mbNo, name)` 으로 갈아 끼운다 (기사/장비 관리 모달이 그렇게 쓴다).
        // 전역 MEMBER 를 임시로 바꿔치기하지 않는다 — 그 사이 다른 모듈이 읽으면 남의 번호를 본다.
        this.mbNo = null;
        this.mbName = '';

        // 인라인 상담영역이 그려진 칸. null 이면 문서 전체에서 찾는다(= 예전 동작).
        // 같은 마크업이 두 곳(열4·기사/장비 관리 모달)에 있을 수 있어 **뿌리를 정해두고 그 안에서만** 찾는다.
        this.$box = null;
    }

    /** 지금 다루는 회원번호 — 지정이 없으면 편집중인 회원 */
    get num() {
        return this.mbNo || (window.MEMBER ? MEMBER['num'] : 0);
    }

    /**
     * 인라인 영역 안에서 찾는다. 뿌리가 없으면 `$(document).find()` 라 `$()` 와 똑같이 동작한다
     * — 열4 하나뿐이던 시절과 결과가 같다.
     */
    _$(sel) {
        return (this.$box && this.$box.length ? this.$box : $(document)).find(sel);
    }

    /**
     * 인라인 상담영역의 마크업 — **열4와 모달이 같은 것을 쓴다.**
     * 예전엔 이 마크업이 블레이드(_col4)에 있어서, 모달에 같은 걸 그리려면 복사해야 했다.
     * 복사한 순간 두 화면이 갈리므로 여기 하나로 모은다.
     */
    inlineHtml() {
        return `
        <div id="show-counsel-list-wrapper" class="counsel-area">
            <div id="showCounselModal" class="cs-editor" style="display:none">
                <div class="cs-editor__row" id="modalMemoForm">
                    <input type="text" id="modal-counsel-date" class="me-input is-date js-date" autocomplete="off">
                    <select id="modalMemoCategory" class="me-select"></select>
                </div>
                <textarea id="counselInput" class="me-input me-input--area" placeholder="내용을 입력하세요"></textarea>
                <div class="cs-editor__actions">
                    <button type="button" class="me-btn me-btn--accent js-counsel-save">확인</button>
                    <button type="button" class="me-btn" id="counselCancel">취소</button>
                </div>
            </div>

            <div class="notebook" id="show-counsel-list"></div>
        </div>`;
    }

    /**
     * 인라인 상담영역을 아무 칸에 그린다 (기사/장비 관리 모달의 상담 칩).
     * 열4와 **같은 마크업·같은 렌더러**를 쓰고, 뿌리만 그 칸으로 옮긴다.
     *
     * ⚠ 되돌리는 것은 부른 쪽 책임이다 (`clearTarget`) — 안 하면 열4가 계속 이 칸을 본다.
     */
    mountInline($box, mbNo, mbName) {
        if (!$box || !$box.length) return;

        $box.html(this.inlineHtml());
        this.$box   = $box;
        this.mbNo   = mbNo ? Number(mbNo) : null;
        this.mbName = mbName || '';
        this.containerHeight = null;          // 칸이 바뀌었으니 높이를 다시 잰다

        this._$('#modalMemoCategory').append(this.typeOptions('상담'));
        this.loadCounsel();
    }

    /** 펼침목록 버튼 — 좁은 칸에서 글자가 세로로 접히지 않게 */
    static get NOWRAP() {
        return 'white-space:nowrap;flex:0 0 auto';
    }

    /** 모달 안 입력 컨트롤 공통 스타일 (v1 은 adm.css 가 없어 클래스에 기댈 수 없다) */
    static get CTRL() {
        return 'height:28px;padding:2px 6px;border:1px solid #adb5bd;border-radius:3px;font-size:12px;box-sizing:border-box';
    }

    /**
     * 유형 select 의 option HTML.
     *
     * '경고'는 없다 — 제재관리에서만 만든다. 목록에 보이는 경고줄은 제재에서 끼워 넣은
     * 읽기전용 줄이라 이 select 를 거치지 않는다 (constructor 주석 참고).
     */
    typeOptions(selected) {
        return Object.keys(this.types)
            .map((t) => `<option value="${t}"${t === selected ? ' selected' : ''}>${t}</option>`)
            .join('');
    }

    /**
     * 상담내역 모달을 연다 ($j.Ui.JBModal).
     *
     * 본문 마크업은 **여기서 만든다.** 예전에는 블레이드에 `#memo-modal` 을 숨겨뒀는데,
     * v1(member/edit)·v2(member/v2/_col4) 두 화면에 같은 id 가 있어서 어느 쪽 노드를 잡는지
     * 화면마다 달랐다. 열 때 만들고 닫히면 통째로 사라지는 편이 안전하다.
     *
     * 그래서 모달 안 이벤트는 전부 document 위임이다 (initCounselHandler 참고).
     */
    openCounselModal(mbNo, mbName) {
        const name = this._targetName(mbNo, mbName);

        $j.Ui.JBModal.html('memo-modal', this.modalHtml(), `상담내역${name ? ' — ' + this._attr(name) : ''}`, { width: '900px' });
        this.startCounsel(mbNo, mbName);

        // ⚠ 닫을 때 **반드시 대상을 되돌린다.** 남겨두면 그 뒤 열4 빠른입력(saveMemo)·목록
        //   (loadCounsel)이 계속 남의 번호를 보고, 사장님 화면에서 쓴 메모가 소속기사에게 저장된다.
        if (mbNo) {
            $j.Common.EventBus.on('$j.F.Admin.Counsel', 'JBModal:close', (e, d) => {
                if (d && d.id !== 'memo-modal') return;
                this.clearTarget();
                $j.Common.EventBus.offNamespace('$j.F.Admin.Counsel');
            });
        }
    }

    /** 전체보기 모달(표)의 대상 지정 + 유형 선택지 + 표 로드 */
    startCounsel(mbNo, mbName) {
        this.mbNo   = mbNo ? Number(mbNo) : null;
        this.mbName = mbName || '';

        // 유형 선택지는 select 가 만들어진 뒤에 채운다.
        // **필터에는 '경고'가 있고 입력 select 에는 없다** — 목록에는 제재에서 끼워 넣은
        // 경고줄이 섞여 있으니 걸러 볼 수 있어야 하지만, 새로 쓸 수는 없기 때문이다.
        $('#type-filter').html(
            `<option value="">유형 전체</option>${this.typeOptions('')}<option value="경고">경고 (제재)</option>`
        );

        this.closeInsertRow();       // 이전에 열어둔 편집줄이 남아 있지 않게
        this.loadCounselTable();
    }

    /** 대상·뿌리를 편집중인 회원(열4)으로 되돌린다 */
    clearTarget() {
        this.mbNo = null;
        this.mbName = '';
        this.$box = null;
        this.containerHeight = null;
    }

    _targetName(mbNo, mbName) {
        if (mbNo) return `${mbName || ''} #${mbNo}`;
        return (window.MEMBER && MEMBER.mem_name) ? `${MEMBER.mem_name} #${MEMBER.num}` : '';
    }

    /**
     * 상담내역 모달 본문 — 검색줄 + 표.
     *
     * 입력줄은 맨 위에 있었는데 없앴다(2026-08). 새 상담은 이제 **표 안에서** 쓴다 —
     * 행의 [추가]를 누르면 그 자리에 편집줄이 열린다(doAdd). 위쪽 폼에 쓰고 저장하면
     * 어디에 들어가는지 보이지 않았고, '삽입 위치'를 따로 기억하는 숨은 상태까지 필요했다.
     *
     * ⚠️ 스타일을 인라인으로 박는다. 이 마크업은 v1(member/edit)과 v2(member/edit-v2) 양쪽에서
     *    쓰였는데 **v1 은 dist/adm.css 를 불러오지 않았다**. 지금은 /adm/member/edit 도
     *    edit-v2 를 그리지만, 이 마크업이 어디서 열리든 혼자 서게 두는 편이 안전하다.
     *    `.me-input` 같은 v2 클래스에 기대면 v1 에서 테두리도 높이도 없는 날것으로 뜬다.
     *    JBModal 껍데기는 Ui/Modal 이 스타일을 스스로 주입하므로 양쪽에서 동일하게 보인다.
     */
    modalHtml() {
        return `
        <div>
            <div style="display:flex;align-items:center;gap:5px;margin-bottom:6px">
                <input type="text" id="find-edit-kwd" placeholder="상담내용 검색" style="${Counsel.CTRL};width:180px">
                <select id="type-filter" style="${Counsel.CTRL};width:auto"></select>
            </div>

            <div id="counsel-table-container">
                <table id="counsel-table" class="display" style="width:100%;border-collapse:collapse">
                    <thead><tr></tr></thead><tbody></tbody>
                </table>
            </div>
        </div>`;
    }

    /**
     * 상담내역 영역 초기화
     */
    initCounsel() {
        // 열4 의 인라인 영역을 그린다 — 마크업 정본은 `inlineHtml()` 하나다 (블레이드는 자리만 준다).
        // 모달 안 표(#counsel-table)의 select 는 여기 없다 — openCounselModal 이 만들 때 채운다.
        $('#counselInline').html(this.inlineHtml());
        this._$('#modalMemoCategory').append(this.typeOptions('상담'));

        this.initCounselHandler();
        this.loadCounsel();
    }

    /**
     * 상담내역 핸들러 등록
     */
    initCounselHandler() {
        let self = this;

        // **먼저 걷어낸다.** 이 함수가 두 번 돌면 위임이 두 벌 붙어 버튼 한 번에 핸들러가
        // 두 번 실행된다 — 확인창이 두 번 뜨고, 저장이 두 번 나간다(짜고치기 판정창이
        // 실제로 그렇게 터졌다). 지금은 기사편집 초기화에서 한 번만 부르지만, 이 모달이
        // 공용이 되는 순간 같은 일이 벌어진다. 네임스페이스를 둔 것은 이 한 줄을 위해서다.
        $(document).off('.counsel');

        // ⚠️ 모달 안 요소(#counsel-table · 삽입 편집줄 · #find-edit-kwd · #type-filter)는
        //    모달을 열 때마다 새로 만들어진다 → 전부 document 위임으로 잡는다.
        //    직접 바인딩하면 이 함수가 도는 시점(초기화)에는 아직 존재하지 않아 그냥 사라진다.

        // 수정 버튼 클릭
        $(document).on('click.counsel', '#counsel-table tbody #editBtn', function (e) {
            e.preventDefault(); // 기본 동작 방지 (이동 방지)

            const $tr = $(this).closest('tr');
            let row = self.$table.row($tr).data();

            self.editRow($tr, row);

            $tr.find('.dropdown-wrapper').hide();
            $tr.find('.action-save-wrapper').show();

        });

        // 모달 열기 ($j.Ui.JBModal — 제재 관리 모달과 같은 엔진)
        $('#openCounsel').on('click', function (e) {
            e.preventDefault();
            self._$('#showCounselModal').hide();
            self.openCounselModal();
        });

        // 작성 모달 열기
        $('#addCounsel').on('click', function (e) {
            e.preventDefault();
            self.openAddCounselModal();
        });

        // 빠른입력 [확인] — 마크업이 JS 로 옮겨오면서 blade 의 onclick 을 위임으로 바꿨다
        $(document).on('click.counsel', '.js-counsel-save', function (e) {
            e.preventDefault();
            self.saveMemo(2);
        });

        // 작성 모달 닫기
        $('#counselCancel').on('click', function (e) {
            e.preventDefault();
            self._$('#showCounselModal').hide();
            self.resizeLayout();
        })

        // 삽입 편집줄 (행의 [추가]가 그 자리에 그린다 — 위임으로 건다)
        $(document).on('click.counsel', '.counsel-insert-row .js-ins-save', function () {
            self.saveInsertRow($(this).closest('tr'));
        });
        $(document).on('click.counsel', '.counsel-insert-row .js-ins-cancel', function () {
            self.closeInsertRow();
        });
        $(document).on('keydown.counsel', '.counsel-insert-row .js-ins-content', function (e) {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            self.saveInsertRow($(this).closest('tr'));
        });

        // 유형 변경 — 서버로 다시 물어본다 (ajax.data 가 #type-filter 를 읽는다)
        $(document).on('change.counsel', '#type-filter', function () {
            if (self.$table) self.$table.ajax.reload(null, false); // false: 현재 페이지 유지
        });

        // 검색어 입력시 바로 검색 되게 (표 안에서 거른다)
        $(document).on('keyup.counsel', '#find-edit-kwd', function () {
            if (self.$table) self.$table.search(this.value).draw();
        });


        // mobiscroll 동작
        //
        // ⚠️ 예전 인자는 `('focus', '#modal-counsel-date, #edit_date', '#counsel-date', fn)` 였다.
        //    jQuery 의 4인자 형태는 (events, selector, data, handler) 라 세번째가 **selector 가
        //    아니라 data** 로 먹혔다 — 즉 모달 안 날짜칸(#counsel-date)에는 달력이 안 붙었다.
        //    셋을 한 selector 로 합친다.
        $(document).on('focus.counsel', '#modal-counsel-date, #edit_date, .js-ins-date', function () {
            $(this).mobiscroll().datepicker({
                controls: ['calendar'],
                dateFormat: 'YYYY-MM-DD',
                locale: mobiscroll.localeKo,
                buttons: [],
                // 머리(이전·오늘·다음)는 전역 기본값을 쓴다 — Common/Mobiscroll.js
            });
        });

        // 드롭다운 버튼 클릭 시 펼치기/접기.
        //
        // ⚠️ 예전 코드는 `'click, mouseover'` 였다. jQuery 는 이벤트 이름을 **공백으로** 끊으므로
        //    이건 `'click,'`(존재하지 않는 이벤트) + `'mouseover'` 로 읽혔다. 즉 클릭은 아무 반응이
        //    없고 마우스만 스쳐도 열렸다 — 행이 수십 개인 표에서 훑어보기만 해도 메뉴가 튀어나왔다.
        //    주석대로 클릭 토글로 고친다(아래 '다른 곳 클릭 시 닫기' 와도 짝이 맞는다).
        $(document).on('click.counsel', '.drop-toggle-btn', function (e) {
            e.stopPropagation();  // 바로 아래 document 핸들러가 방금 연 것을 다시 닫지 않게
            const $wrapper = $(this).closest('.dropdown-wrapper');

            // 모든 다른 드롭다운 닫기
            $('.dropdown-wrapper').not($wrapper).removeClass('show');
            // 현재 클릭한 것만 토글
            $wrapper.toggleClass('show');
        });

        // 다른 곳 클릭 시 닫기
        $(document).on('click.counsel', function () {
            $('.dropdown-wrapper').removeClass('show');
        });

        // 검색창에서 엔터 막기 (폼 전송 방지)
        $(document).on('keydown.counsel', '#find-edit-kwd', function (e) {
            if (e.key === 'Enter') {
                e.preventDefault();
            }
        });

        // modalForm에서 버튼 클릭시 동작 핸들러로 전송 ( action : 'add','save','delete','cancel' )
        // ⚠️ `.action-btn` 이 아니라 `[data-action]` 으로 잡는다 — 편집중 확인(#editSaveBtn)은
        //    이제 `.me-btn--accent` 로 그려지는데, `#memo-modal .action-btn`(ID 특이도)이
        //    섞여 있으면 그 스타일을 도로 이긴다. 클릭 위임은 클래스가 아니라 의미(동작)로 건다.
        $(document).on('click.counsel', '#counsel-table [data-action]', function (e) {
            e.preventDefault();

            const action = $(this).data('action');
            const $tr = $(this).closest('tr');
            const rowData = self.$table.row($tr).data();
            self.actionHandler(action, $tr, rowData);
        })

        // 롱클릭 → **그 자리에서** 편집 (예전에는 위쪽 빠른입력 폼에 값을 채워 넣었다.
        // 목록 맨 위로 시선이 튀고, 어느 줄을 고치는 중인지 화면에 표시가 없었다.)
        // 인라인 목록은 열4·모달 두 곳에 있을 수 있어 **document 위임**으로 잡는다.
        // 컨테이너에 직접 걸면 나중에 만들어지는 쪽(모달)은 핸들러가 없다.
        $(document).on('mousedown.counsel touchstart.counsel', '#show-counsel-list .notebook-entry', function(e) {
            // 제재에서 온 줄 — 상담내역에서는 읽기만 한다
            if ($(this).hasClass('is-from-penalty')) return;
            const $entry = $(this);
            // 편집기 안(입력·버튼)에서 시작한 누름은 롱클릭이 아니다
            if ($(e.target).closest('.cs-editor').length) return;
            if ($entry.hasClass('is-editing')) return;

            let timer = setTimeout(function() {
                $entry.removeClass('long-pressed');
                self.openInlineEdit($entry);
            }, 200);
            $entry.addClass('long-pressed'); // 누른효과
            $entry.on('mouseup mouseleave touchend touchcancel', function() {
                clearTimeout(timer);
                $entry.off('mouseup mouseleave touchend touchcancel');
                $(this).removeClass('long-pressed'); // 손 떼면 원래대로
            });
        });

        // 인라인 편집기 버튼 (편집기는 롱클릭 때 그려지므로 위임으로 건다)
        // 목록은 열4·기사/장비 관리 모달 두 곳에 있을 수 있어 **document 위임**으로 잡는다.
        // 컨테이너에 직접 걸면 나중에 만들어지는 쪽(모달)은 핸들러가 없다.
        // 끼워넣기 편집줄 (`.cs-editor--ins`) — 편집기와 버튼 규약이 달라 따로 잡는다
        $(document).on('click.counsel', '#show-counsel-list .cs-editor--ins .me-btn', function(e) {
            e.stopPropagation();
            const $ins = $(this).closest('.cs-editor--ins');
            if ($(this).data('act') === 'ins-save') self.saveInlineInsert($ins);
            else $ins.remove();
        });

        $(document).on('click.counsel', '#show-counsel-list .cs-editor__actions .me-btn', function() {
            const $editor = $(this).closest('.cs-editor');
            switch ($(this).data('act')) {
                case 'save':   self.saveInlineEdit($editor); break;
                case 'delete': self.deleteInlineEdit($editor); break;
                case 'add':    self.openInlineInsert($editor); break;
                default:       self.closeInlineEdit();
            }
        });
    }

    /**
     * 상담 한 줄을 그 자리에서 편집 — 날짜 · 유형 · 내용.
     * 저장은 목록/표 모달과 같은 update 엔드포인트를 탄다.
     */
    openInlineEdit($entry) {
        const self = this;
        const $content = $entry.find('.notebook-content');
        const content = $content.attr('data-content');
        const num = $content.attr('data-num');

        // 상담내역 존재 여부 판단 ('상담 기록이 없습니다' 안내행은 편집 대상이 아니다)
        const hasCounsel = $content.length > 0 && num
            && typeof content !== 'undefined'
            && String(content).trim() !== ''
            && String(content).trim().toLowerCase() !== 'null';

        if (!hasCounsel) {
            self.openAddCounselModal();
            return;
        }

        self.closeInlineEdit();   // 한 번에 한 줄만 편집한다

        let date = $content.attr('data-date');
        if (!date || date === 'null') date = '';

        const type = self.types[$content.attr('data-type')] ? $content.attr('data-type') : '상담';
        const options = self.typeOptions(type);

        // .cs-editor — 빠른입력(#showCounselModal)과 같은 카드·순서(메타 행 → 내용 → 액션 행).
        const $editor = $(`
            <div class="cs-editor" data-num="${self._attr(num)}" data-odr="${self._attr($content.attr('data-odr'))}">
                <div class="cs-editor__row">
                    <input type="text" class="me-input is-date cs-editor__date" value="${self._attr(date)}" autocomplete="off">
                    <select class="me-select cs-editor__type">${options}</select>
                </div>
                <textarea class="me-input me-input--area cs-editor__text" rows="3"></textarea>
                <div class="cs-editor__actions">
                    <button type="button" class="me-btn" data-act="delete">삭제</button>
                    <button type="button" class="me-btn" data-act="add"
                            data-tip-hover data-tip="이 상담 바로 위에 새 상담을 끼워 넣습니다. 상담내역은 날짜순이 아니라 CS 가 정한 순서라, 중간에 넣을 자리를 여기서 고릅니다."
                    >추가</button>
                    <span class="cs-editor__gap"></span>
                    <button type="button" class="me-btn me-btn--accent" data-act="save">확인</button>
                    <button type="button" class="me-btn" data-act="cancel">취소</button>
                </div>
            </div>`);

        $editor.find('.cs-editor__text').val(content);
        $entry.addClass('is-editing');
        $content.hide().after($editor);
        $editor.find('.cs-editor__text').focus();

        // 날짜 달력. 편집기는 롱클릭 때마다 새로 만드는 요소라 여기서 바로 붙인다 —
        // 다른 날짜칸처럼 focus 위임으로 붙이면 이미 focus 가 지나간 뒤라 첫 클릭이 헛돈다.
        if (typeof mobiscroll !== 'undefined') {
            $editor.find('.cs-editor__date').mobiscroll().datepicker({
                controls: ['calendar'],
                dateFormat: 'YYYY-MM-DD',
                locale: mobiscroll.localeKo,
                buttons: [],
                // 머리(이전·오늘·다음)는 전역 기본값을 쓴다 — Common/Mobiscroll.js
            });
        }
    }

    /**
     * [추가] — 이 상담 **바로 위에** 끼워 넣을 편집줄을 연다.
     *
     * 상담내역은 날짜순이 아니라 `odr`(회원별 수동 정렬)순이라 중간에 넣을 일이 있다.
     * 서버가 `UPDATE odr = odr+1 WHERE odr >= ?` 로 아래를 한 칸씩 밀고 넣으므로
     * (`CounselController::addMemberCounsel`) 새 상담은 **누른 줄의 위**에 자리잡는다.
     * 편집줄을 같은 자리에 그리는 이유가 이것이다 — 보이는 위치와 저장되는 위치를 맞춘다.
     * 표 모달의 `doAdd` 와 같은 규약이고, 여기서는 목록이 표가 아니라 카드라 마크업만 다르다.
     */
    openInlineInsert($editor) {
        const odr = $editor.data('odr');
        const $entry = $editor.closest('.notebook-entry');

        this._$('#show-counsel-list .cs-editor--ins').remove();   // 한 번에 하나만 연다

        const $ins = $(`
            <div class="cs-editor cs-editor--ins" data-odr="${this._attr(odr)}">
                <div class="cs-editor__row">
                    <input type="text" class="me-input is-date cs-editor__date" autocomplete="off">
                    <select class="me-select cs-editor__type">${this.typeOptions('상담')}</select>
                </div>
                <textarea class="me-input me-input--area cs-editor__text" rows="3"
                          placeholder="여기에 쓰면 아래 상담 위에 들어갑니다"></textarea>
                <div class="cs-editor__actions">
                    <button type="button" class="me-btn me-btn--accent" data-act="ins-save">저장</button>
                    <button type="button" class="me-btn" data-act="ins-cancel">취소</button>
                </div>
            </div>`);

        $ins.find('.cs-editor__date').val(dayjs().format('YYYY-MM-DD'));
        $entry.before($ins);
        $ins.find('.cs-editor__text').trigger('focus');

        // 날짜 달력 — 편집기와 같은 이유로 여기서 바로 붙인다(위임은 첫 클릭을 놓친다)
        if (typeof mobiscroll !== 'undefined') {
            $ins.find('.cs-editor__date').mobiscroll().datepicker({
                controls: ['calendar'],
                dateFormat: 'YYYY-MM-DD',
                locale: mobiscroll.localeKo,
                buttons: [],
            });
        }
    }

    /** 끼워넣기 저장 — 표 모달의 `saveInsertRow` 와 **같은 엔드포인트·같은 인자** */
    saveInlineInsert($ins) {
        const content = $.trim($ins.find('.cs-editor__text').val());
        if (content === '') {
            $j.F.Admin.Util.tsalert('error', '상담 내용을 입력해주세요', 'bottom-right');
            return $ins.find('.cs-editor__text').trigger('focus');
        }

        $.ajax({
            url:  `/adm/ajax/member/${this.num}/counsel/add`,
            type: 'post',
            data: {
                content,
                type:         $ins.find('.cs-editor__type').val(),
                counsel_date: $ins.find('.cs-editor__date').val(),
                odr:          $ins.data('odr'),
            },
        }).done((e) => {
            if (!e || !e.result) { $j.F.Admin.Util.tsalert('error', (e && e.message) || '추가하지 못했습니다.', 'bottom-right'); return; }
            $j.F.Admin.Util.tsalert('success', e.message, 'bottom-right');
            $ins.remove();
            this.closeInlineEdit();
            this.loadCounsel();
        }).fail((xhr) => $j.F.Admin.Util.tsalert('error', `상담 추가 중 오류가 발생했습니다. (${xhr.status})`));
    }

    /**
     * 그 자리에서 삭제 — 목록/표 모달의 [삭제]와 **같은 엔드포인트**를 탄다.
     * 되돌릴 수 없으므로 반드시 묻는다.
     */
    deleteInlineEdit($editor) {
        const num = $editor.data('num');
        if (!num) { $j.F.Admin.Util.tsalert('error', '삭제할 상담을 찾지 못했습니다.'); return; }

        Util.swalert(['이 상담 내역을 삭제할까요?', 'question'], () => {
            $.ajax({
                url:  `/adm/ajax/member/${this.num}/counsel`,
                type: 'delete',
                data: { num },
            }).done((e) => {
                if (!e || !e.result) { $j.F.Admin.Util.tsalert('error', (e && e.message) || '삭제하지 못했습니다.'); return; }
                $j.F.Admin.Util.tsalert('success', e.message, 'bottom-right');
                this.closeInlineEdit();
                this.loadCounsel();
            }).fail((xhr) => $j.F.Admin.Util.tsalert('error', `삭제 중 오류가 발생했습니다. (${xhr.status})`));
        }, ['네', '아니오']);
    }

    /** 편집 취소 — 편집기를 걷고 원래 줄을 되돌린다 */
    closeInlineEdit() {
        this._$('#show-counsel-list .cs-editor').remove();
        this._$('#show-counsel-list .notebook-entry.is-editing')
            .removeClass('is-editing')
            .find('.notebook-content').show();
    }

    saveInlineEdit($editor) {
        const self = this;
        const content = $editor.find('.cs-editor__text').val();

        if ($.trim(content) === '') {
            $j.F.Admin.Util.tsalert('error', '상담 내용을 입력해주세요', 'bottom-right');
            return;
        }

        const rowData = {
            num: $editor.data('num'),
            content: content,
            type: $editor.find('.cs-editor__type').val(),
        };
        const date = $editor.find('.cs-editor__date').val();
        if (date) rowData['counsel_date'] = date;

        $.ajax({
            url: `/adm/ajax/member/${this.num}/counsel/update`,
            type: 'post',
            data: rowData,
            success: function (e) {
                if (e.result) {
                    $j.F.Admin.Util.tsalert('success', e.message, 'bottom-right');
                    self.closeInlineEdit();
                    self.loadCounsel();
                    if (self.$table) self.$table.ajax.reload(null, false);
                }
            },
            error: function () {
                alert('수정 중 오류 발생!');
            }
        });
    }
    // 상담내역의 높이를 조절
    resizeLayout(){
        if(!this.containerHeight) return; // 편집 버튼누르기전이라면 높이조절 안해도된다.
        // console.log( 'resizeLayout', this.containerHeight);
        const $q = this._$('#showCounselModal');
        const aHeight = $q.is(':visible') ? $q.outerHeight(true) : 0;
        console.log( 'aHeight', aHeight);
        this._$('#show-counsel-list').height(this.containerHeight - aHeight);
    }
    // 상담내역의 맨아래로 이동
    scrollToBottom(){
        const $l = this._$('#show-counsel-list');
        if ($l.length) $l.scrollTop($l[0].scrollHeight);
    }

    actionHandler(action, $tr, rowData) {
        console.log(action);
        let self = this;
        switch (action) {
            case 'edit':
                self.doEdit($tr);
                break;
            case 'add':
                self.doAdd($tr, rowData);
                break;
            case 'delete':
                self.doDelete($tr, rowData);
                break;
            case 'cancel':
                self.$table.ajax.reload();
            // toggleMemoContainer(false);
            default:
                console.log('here');
        }
    }


    /**
     * 상담 테이블
     *
     * 행 버튼 세 개 중 [추가]는 **그 행 위에 편집줄을 연다**(doAdd). 맨 뒤에 붙이는 것이 아니라
     * 그 자리에 끼워 넣는다 — 상담내역이 날짜순이 아니라 odr(회원별 수동 정렬)순이기 때문이다.
     */
    loadCounselTable() {
        let self = this;

        // 짧은 값(일자·처리자·유형)은 줄바꿈을 막는다. '2026-08-06' 이 두 줄로 접히면
        // 행 높이가 들쭉날쭉해져 목록을 훑기 어렵다.
        const nowrap = (v) => `<span style="white-space:nowrap">${self._attr(v ?? '')}</span>`;

        const columns = [

            {data: 'counsel_date', title: '일자', render: nowrap},
            {data: 'manage_name', title: '처리자', render: nowrap},
            {data: 'content', title: '상담내용', render: (v) => self._attr(v)},
            {data: 'type', title: '유형', render: nowrap},
            {
                data: null, title: '버튼', render: function (data, type, row) {
                    // 제재에서 끼워 넣은 줄은 여기서 고칠 수 없다 — 쓰기는 제재관리 한 곳이다
                    // 해제된 경고는 서버가 아예 내리지 않는다 (CounselController::mergeWarnings)
                    if (row.src === 'penalty') {
                        return `<span class="cs-from-pen" title="제재관리에서 등록한 경고입니다. 수정·삭제는 제재관리에서 합니다.">제재</span>`;
                    }
                    return `
             <div class="action-buttons">
                <!-- min-width:0 — _member-edit-v2.scss 가 이 래퍼에 min-width:140px 을 걸어
                     버튼칸이 통째로 넓어지고 일자칸이 두 줄로 접혔다.
                     펼침목록(.emoji-options)은 absolute 라 래퍼가 좁아도 잘리지 않는다. -->
                <div class="dropdown-wrapper" style="min-width:0">
                    <button class="drop-toggle-btn"><i class="fa-solid fa-gear"></i></button>
                    <!-- 펼침목록은 좁아진 버튼칸(96px)보다 넓다. 기본값(left:0)이면 오른쪽으로 뻗어
                         모달 밖에서 잘리고, 폭이 모자라 글자가 세로로 접힌다.
                         → 오른쪽 끝에 맞춰 **왼쪽으로** 펼치고 줄바꿈을 막는다.
                         이 모듈은 여러 화면이 공유하므로 시트마다 고치는 대신 여기 인라인으로 누른다.
                         display 는 넣지 말 것 — .show 토글이 깨진다. -->
                    <div class="emoji-options" style="left:auto;right:0;min-width:0;flex-wrap:nowrap">
                      <button id="editBtn" class="edit-btn action-btn" style="${Counsel.NOWRAP}">수정</button>
                      <button id="addBtn" class="add-btn action-btn" data-action="add" style="${Counsel.NOWRAP}">추가</button>
                      <button id="deleteBtn" class="delete-btn action-btn" data-action="delete" style="${Counsel.NOWRAP}">삭제</button>
                    </div>
                </div>
                <div class="action-save-wrapper" style="display:none;white-space:nowrap">
                    <button id="editSaveBtn" class="me-btn me-btn--accent" data-action="edit">확인</button>
                    <button id="cancelEditBtn" class="me-btn" data-action="cancel">취소</button>
                </div>
              </div>
    `;
                }
            },
            {data: 'num', title: '번호', visible: false},
            {data: 'mb_no', title: '회원번호', visible: false},
            {data: 'odr', title: '순서', visible: false}
        ];

        // 모달을 닫으면 표 노드가 통째로 사라지므로 destroy() 가 붙잡을 것이 없다.
        // 남아 있을 때만 정리하고, 없으면 참조만 버린다 (JBModal 로 옮기며 추가).
        if (self.$table) {
            if (document.getElementById('counsel-table') && self.$table.table().node()?.isConnected) {
                try { self.$table.destroy(); } catch (e) { /* 이미 떨어져 나간 노드 */ }
                $('#counsel-table').empty().append('<thead><tr></tr></thead><tbody></tbody>');
            }
            self.$table = null;
        }
        const mbNo = this.num;
        self.$table = new DataTable('#counsel-table', {
            autoWidth: false,
            columns: columns,
            // 폭은 px 로 고정한다. %(합 98%)로 주면 버튼칸 내용이 넓어질 때 일자칸부터 밀린다.
            // 상담내용(targets:2)만 남는 폭을 전부 먹는다.
            columnDefs: [
                {targets: 0, width: '92px'},   // 일자 — 'YYYY-MM-DD' 한 줄
                {targets: 1, width: '72px'},   // 처리자
                {targets: 3, width: '64px'},   // 유형
                {targets: 4, width: '120px'},  // 버튼 — 편집중 [확인][취소](.me-btn 두 개)가 들어갈 만큼
            ],
            // DataTables 가 rowCallback 을 인스턴스에 안 묶어서 부르므로 bind 없인 formatRow 안의
            // this.WARN_BG/this.WARN_CLOSED_BG 가 undefined 로 빠져 색이 하나도 안 먹었다.
            rowCallback: self.formatRow.bind(self),
            ajax: {
                url: `/adm/ajax/member/${mbNo}/counsels`,
                type: 'post',

                data: function (d) {
                    d.num = mbNo;
                    d.type = $('#type-filter').val();
                    d.search = $('#find-edit-kwd').val();
                }
            },
            drawCallback: function () {
                self.hideSaveBtn();
            },
            headerCallback: (thead) => $(thead).find('th').css(
                {
                    "font-size": "14px",
                    "padding": "2px",
                    "text-align": "center",
                    "font-weight": "bold"
                }),
            // 하단 = 좌 건수 · 우 페이지번호 (관리자 표 공통 규약)
            layout: { topStart: null, topEnd: null, bottomStart: 'info', bottomEnd: 'paging' },
            // 정렬은 서버가 정한 순서(ORDER BY odr ASC)를 그대로 쓴다.
            //
            // ⚠️ odr 컬럼으로 초기정렬을 걸지 말 것 — Sql 이 모든 값을 문자열로 돌려주므로
            //    DataTables 가 문자열로 비교해 10 이 2 보다 앞에 온다.
            //    (예전 값은 `[[9,'asc']]` 였는데 컬럼이 8개뿐이라 존재하지 않는 인덱스였다)
            order: [],
            processing: true,
            serverSide: false,
            ordering: true,
            paging: true,
            pageLength: 10,      // 10개 단위
            lengthChange: false, // 개수 선택은 안 준다 (고정 10)
            searching: true,
            info: true,
            language: {
                info: '_START_–_END_ / 전체 _TOTAL_건',
                infoEmpty: '내역 없음',
                infoFiltered: '(전체 _MAX_건 중 검색)',
                emptyTable: '상담 내역이 없습니다.',
                zeroRecords: '검색 결과가 없습니다.',
                paginate: { first: '«', previous: '‹', next: '›', last: '»' },
            },
        });


    }

    /**
     * 타입 유형에 따라 배경색 설정
     * @param row
     * @param data
     */
    formatRow(row, data) {
        const colMap = {
            '경고': this.WARN_BG,
            '가입': '#fffce3',
            '탈퇴': '#edf4fc',
        };
        // 제재에서 끼워 넣은 줄 — 여기서는 고칠 수 없다는 것을 색 말고 구조로도 알린다
        if (data.src === 'penalty') $(row).addClass('is-from-penalty');

        if (data.src === 'penalty' && data.is_case_closed) {
            // 풀린 경고는 부여·해제 두 줄 다 회색 — 지금 걸려있는지 이미 끝났는지 색만 보고 구분되게
            $(row).css('background-color', this.WARN_CLOSED_BG);
        } else if (colMap[data.type]) {
            $(row).css('background-color', colMap[data.type]);
        } else {
            $(row).css('background-color', 'var(--adm-b-ffffff, #ffffff)');
        }
        if( data.type=='중요') {
            $(row).find('td').css('font-weight', 'bold');
        }

    }

    /**
     * 확인 버튼 숨기기
     */
    hideSaveBtn() {
        $('#counsel-table').find('.dropdown-wrapper').show();

        // 모든 행의 저장 버튼 숨기기
        $('#counsel-table').find('.action-save-wrapper').hide();
    }

    /**
     * 작성 모달 열기 공통 함수
     */
    openAddCounselModal() {
        const self = this;

        // 최초에 컨테이너의 높이를 파악.
        if (!self.containerHeight) {
            self.containerHeight = self._$('#show-counsel-list-wrapper').height();
        }

        self.isEdit = false; // 작성 모드

        self._$('#showCounselModal').toggle(); // 기존 동작을 그대로 유지
        self._$('#modal-counsel-date').val(dayjs().format('YYYY-MM-DD')).show();
        self._$('#counselInput')
            .val('')
            .removeData('type')
            .removeData('date')
            .removeData('num')
            .focus();

        self.resizeLayout();
    }

    /**
     * 상담 내역 수정시 인풋 모양.
     *
     * 이 화면 전역 입력 클래스(.me-input/.me-select)를 그대로 쓴다 — 추가행(doAdd)과
     * 같은 클래스라 폭·테두리·글자크기가 저절로 맞는다(2026-09, 예전엔 이 표 전용
     * `.edit-textarea`/`.edit-select` 를 따로 그려서 모양이 서로 달랐다).
     */
    editRow(row, data) {
        let self = this;
        $(row).find('td').each(function (index) {
            let content = $(this).text();
            if (index === 2) {
                $(this).html(`<input type="text" class="me-input" value="${self._attr(content)}">`);
            } else if (index === 3) {
                $(this).html(`<select class="me-select">${self.typeOptions(content)}</select>`);
            } else if (index == 0) {
                $(this).html(`<input type="text" class="me-input is-date" id="edit_date" name="edit_date" value="${self._attr(content)}" autocomplete="off">`);
            }
        });
    }

    doEdit(row) {
        let self = this;
        let rowData = {};
        row.find('td').each(function (index) {
            if (index !== 4) { // 액션 버튼 제외
                let inputElement = $(this).find('input, select, textarea');
                let value = inputElement.is('select') ? inputElement.val() : inputElement.val();


                const columnName = self.$table.column(index).dataSrc(); // 컬럼 이름 가져오기
                rowData[columnName] = value;  // column 이름에 해당하는 값을 rowData에 저장
            }
        });
        let rowDataNum = self.$table.row(row).data().num;
        let rowDataMbNo = self.$table.row(row).data().mb_no;

        rowData['num'] = rowDataNum;

        $.ajax({
            url: `/adm/ajax/member/${this.num}/counsel/update`,
            type: 'post',
            data: rowData,
            success: function (e) {
                if (e.result) {
                    $j.F.Admin.Util.tsalert('success', e.message,'bottom-right');
                    self.loadCounsel();
                    self.$table.ajax.reload(null, false);
                }
            },
            error: function () {
                alert('수정 중 오류 발생!');
            }
        });
    }

    // 추가 버튼 클릭
    /**
     * [추가] — 누른 행 **바로 위에 편집줄을 연다.** 거기 쓰고 저장하면 그 자리에 들어간다.
     *
     * 상담내역은 날짜순이 아니라 odr(회원별 수동 정렬)순이라 중간에 끼워 넣을 일이 있다.
     * 서버가 `UPDATE odr = odr+1 WHERE odr >= ?` 로 아래를 한 칸씩 밀고 넣으므로
     * (CounselController::addMemberCounsel) 새 상담은 **누른 행의 위**에 자리잡는다.
     * 편집줄을 같은 자리에 그리는 이유가 이것이다 — 보이는 위치와 저장되는 위치를 맞춘다.
     *
     * 예전에는 위치만 기억해두고 모달 맨 위 입력줄에 쓰게 했는데, 어디로 들어가는지 보이지 않았고
     * 저장 없이 닫으면 그 위치가 유령처럼 남았다.
     */
    doAdd(row, rowData) {
        this.closeInsertRow();   // 한 번에 하나만 연다

        // 실제 열 구조(일자·처리자·내용·유형·버튼) 그대로 <td> 를 채운다 — editRow(수정)와
        // 같은 모양이 되게(2026-09). 처리자는 서버가 정하므로(등록자=현재 관리자) 입력칸이 아니다.
        const $editor = $(`
            <tr class="counsel-insert-row">
                <td><input type="text" class="me-input is-date js-ins-date" autocomplete="off"></td>
                <td class="counsel-insert-row__manager">—</td>
                <td><input type="text" class="me-input js-ins-content" placeholder="상담 내용을 입력하세요"></td>
                <td><select class="me-select js-ins-type">${this.typeOptions('상담')}</select></td>
                <td class="counsel-insert-row__actions">
                    <button type="button" class="me-btn me-btn--accent js-ins-save">저장</button>
                    <button type="button" class="me-btn js-ins-cancel">취소</button>
                </td>
            </tr>`);

        // 서버로 보낼 삽입 위치. DOM 에 붙여두면 유령 상태가 남지 않는다 — 줄이 사라지면 같이 사라진다
        $editor.data('odr', rowData['odr']);
        $editor.find('.js-ins-date').val(dayjs().format('YYYY-MM-DD'));

        $(row).before($editor);
        $editor.find('.js-ins-content').trigger('focus');
    }

    /** 열려 있는 삽입 편집줄을 닫는다 */
    closeInsertRow() {
        $('.counsel-insert-row').remove();
    }

    /** 삽입 편집줄 저장 */
    saveInsertRow($editor) {
        const self = this;
        const content = $.trim($editor.find('.js-ins-content').val());

        if (content === '') {
            $j.F.Admin.Util.tsalert('error', '상담 내용을 입력해주세요', 'bottom-right');
            return $editor.find('.js-ins-content').trigger('focus');
        }

        $.ajax({
            url: `/adm/ajax/member/${this.num}/counsel/add`,
            type: 'post',
            data: {
                content,
                type: $editor.find('.js-ins-type').val(),
                counsel_date: $editor.find('.js-ins-date').val(),
                odr: $editor.data('odr'),
            },
            success: function (e) {
                if (!e.result) return $j.F.Admin.Util.tsalert('error', e.message, 'bottom-right');

                $j.F.Admin.Util.tsalert('success', e.message, 'bottom-right');
                self.closeInsertRow();
                self.$table.ajax.reload(null, false);
                self.loadCounsel();
            },
            error: function () {
                alert('상담 추가 중 오류 발생!');
            },
        });
    }

    doDelete(row, rowData) {
        let self = this;
        Util.swalert(["선택한 상담 내역을 삭제할까요?", 'question'], () => {
            $.ajax({
                url: `/adm/ajax/member/${this.num}/counsel`,
                type: 'delete',
                data: rowData,
                success: function (e) {
                    if (e.result) {
                        $j.F.Admin.Util.tsalert('success', e.message,'bottom-right');
                        self.loadCounsel();
                        self.$table.ajax.reload(null, false);
                    }
                },
                error: function () {
                    alert('삭제 중 오류 발생!');
                }
            });
        }, ['네', '아니오']);
    };

    /**
     * 상담 저장 — 열4 **빠른입력**(#showCounselModal) 전용.
     *
     * 모달 안 추가는 여기를 타지 않는다. 표에서 [추가]를 누르면 그 자리에 편집줄이 열리고
     * saveInsertRow() 가 저장한다(2026-08, 모달 상단 입력줄 폐지).
     *
     * @param type 2 만 쓴다 (노트 모달에서 추가/수정). 1 은 폐지된 상단 입력줄이었다.
     */
    saveMemo(type = 2) {
        let self = this;
        let memoText = this._$('#counselInput').val();
        let date = this._$('#modal-counsel-date').val();
        let memoCategory = this._$('#modalMemoCategory').val();

        if ($.trim(memoText) === "") {
            $j.F.Admin.Util.tsalert("error", "상담 내용을 입력해주세요",'bottom-right');
            return;
        }

        let rowData = {
            content: memoText,
            type: memoCategory,
        };

        if (date) {
            rowData['counsel_date'] = date;
        }

        if (self.isEdit){
            rowData['num'] = this._$('#counselInput').data('num');

            $.ajax({
                url: `/adm/ajax/member/${this.num}/counsel/update`,
                type: 'post',
                data: rowData,
                success: function (e) {
                    if (e.result) {
                        $j.F.Admin.Util.tsalert('success', e.message,'bottom-right');
                        self._$('#showCounselModal').hide();
                        self.loadCounsel();
                    }
                },
                error: function () {
                    alert('수정 중 오류 발생!');
                }
            });
        } else {
            $.ajax({
                url: `/adm/ajax/member/${this.num}/counsel/add`,
                type: 'post',
                data: rowData,
                success: function (e) {
                    if (e.result) {
                        $j.F.Admin.Util.tsalert('success', e.message,'bottom-right')
                        self.scrollToBottom();
                        self._$('#showCounselModal').hide();
                        self.loadCounsel();
                    }
                },
                error: function () {
                    alert('수정 중 오류 발생!');
                }
            });
        }
    }


    /**
     * 관리자페이지 메모 불러오기
     */
    loadCounsel() {
        const self = this;
        let mbNo = this.num;
        $.ajax({
            url: `/adm/ajax/member/${mbNo}/counsels`,
            type: 'get',
            success: function (res) {
                // console.log(res);
                // res.data 기준 (DataTables 스타일 응답이라면)
                const list = res.data || res;

                let html = '';

                if (list.length === 0) {
                    // is-empty : 안내문일 뿐 기록이 아니므로 구분선 대상에서 뺀다(v2 스타일)
                    html = `<div class="notebook-entry is-empty">
                            <div class="notebook-content">상담 기록이 없습니다.</div>
                        </div>`;
                } else {
                    list.forEach(item => {
                        let manager = item.manage_name;
                        let counselDate = '';
                        if (item.counsel_date) {
                            counselDate = item.counsel_date.split("-").map((v, i) => i == 0 ? v.slice(2) : parseInt(v)).join(".");
                        }

                        let content = item.content;

                        let header = (counselDate || manager)
                            ? `${Util.nvl(counselDate)} ${self._attr(Util.nvl(manager))} `
                            : '';

                        let convertedContent = self._attr(content)
                            .replace(/ /g, '&nbsp;')
                            .replace(/\n/g, '<br>');

                        let counselContent = header + ' ㅡ ' + convertedContent;

                        let bgColor = self.types[item.type] || '#ffffff';
                        let boldStyle = '';
                        if( item.type=='중요'){
                            boldStyle = 'font-weight: bold;';
                        }
                        // data-content 는 인라인 편집기가 원문을 되읽는 값이라 속성 이스케이프가 필요하다
                        // (따옴표가 든 상담이 들어오면 속성이 끊겨 편집기가 빈 값으로 열렸다)
                        // 제재에서 끼워 넣은 줄은 편집기를 열지 않는다 (.is-from-penalty 로 판정)
                        const fromPen = item.src === 'penalty';
                        if (fromPen) bgColor = item.is_case_closed ? self.WARN_CLOSED_BG : self.WARN_BG;
                        html += `<div class="notebook-entry${fromPen ? ' is-from-penalty' : ''}">
                                <div class="notebook-content" style="background: ${bgColor}; ${boldStyle}" data-type="${self._attr(item.type)}" data-date="${self._attr(item.counsel_date)}" data-num="${self._attr(item.num)}" data-odr="${self._attr(item.odr)}" data-content="${self._attr(item.content)}">
                                    ${counselContent}
                                </div>
                             </div>`;
                    });

                }

                self._$('#show-counsel-list').html(html);
                self.resizeLayout();
            }
        });
    }

    /** HTML 속성값 이스케이프 (data-content 처럼 원문을 속성에 실을 때) */
    _attr(v) {
        return String(v ?? '').replace(/[&<>"']/g, (c) => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
        }[c]));
    }

    showCounselForm(type = 'counsel') {
        let self = this;
        // 최초에 컨테이너의 높이를 파악해오자.
        if(!self.containerHeight){
            self.containerHeight = self._$('#show-counsel-list-wrapper').height();
        }
        self.resizeLayout();
        self._$('#showCounselModal').show();
        if (type === 'counsel') {
            self._$('#modal-counsel-date').val(dayjs().format('YYYY-MM-DD')).show();
            self._$('#modalMemoCategory').val('상담');
            self._$('#counselInput').val('').focus();
        } else if (type === 'edit') {

        }


    }
    editContent() {
        this.showCounselForm();
    }
}